// Cliente HTTP al backend HelpyDoc (Fastify /api/v1)

import { AuthService } from '@/services/AuthService';
import type {
  ActividadHoy,
  Comida,
  Emocion,
  Ejercicio,
  Hidratacion,
  Plato,
  ResumenNutricion,
  Usuario,
} from '@/services/DataService';
import type { WeightEntry } from '@/models/weight';
import type {
  AnalysisItem,
  AnalysisStatus,
  NutritionAnalysisJob,
  NutritionAnalysisResult,
} from '@/models/analysis';
import type {
  EntradaModeloTransmitida,
  EstimacionPreviaTransmitida,
} from '@/lib/entrada-modelo/contrato';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) {
    throw new ApiError('VITE_API_BASE_URL no configurada', 0, 'CONFIG');
  }
  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(init.headers ?? {}),
  };
  // getIdToken() siempre devuelve un token fresco:
  // - mock: session.token (localStorage)
  // - Firebase: user.getIdToken() (renueva automáticamente si expira en 1h)
  const token = await AuthService.getIdToken();
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    error?: { code?: string };
  };

  if (!res.ok) {
    throw new ApiError(friendlyApiMessage(res.status, res.headers, body.message), res.status, body.error?.code);
  }
  return body as T;
}

/**
 * Traduce estados HTTP de error conocidos a un mensaje claro para el usuario,
 * con acción siguiente. Complementa el hardening de backend: 429 (rate limit,
 * Q4), 503 (servicio externo caído, p.ej. USDA — Q14), 502/504 (proxy/timeout).
 * Para el resto usa el mensaje del backend si es legible.
 */
export function friendlyApiMessage(
  status: number,
  headers?: Pick<Headers, 'get'>,
  backendMessage?: string,
): string {
  const backend = backendMessage?.trim();
  // El paciente nunca debe ver detalles de coste/presupuesto de IA (solo el admin):
  // si el texto del backend los menciona, se descarta y se usa el mensaje neutro.
  const safeBackend =
    backend && !/coste|costo|cost|presupuesto|budget|l[íi]mite|limit|cuota|quota|gasto|\busd\b|d[óo]lar|\$/i.test(backend)
      ? backend
      : undefined; // \busd\b evita colisión con "USDA"
  switch (status) {
    case 429: {
      const retry = Number(headers?.get('retry-after'));
      return Number.isFinite(retry) && retry > 0
        ? `Demasiadas peticiones seguidas. Vuelve a intentarlo en ${retry}s.`
        : 'Demasiadas peticiones seguidas. Espera un momento y reinténtalo.';
    }
    case 503:
      return safeBackend || 'El servicio no está disponible ahora mismo. Inténtalo en un momento.';
    case 502:
    case 504:
      return 'Problema temporal de conexión con el servidor. Inténtalo de nuevo.';
    default:
      return safeBackend || `HTTP ${status}`;
  }
}

function unwrapData<T>(payload: { data?: T }): T {
  if (payload.data === undefined) {
    throw new ApiError('Respuesta API sin campo data', 502, 'INVALID_RESPONSE');
  }
  return payload.data;
}

function unwrapList<T>(payload: { data?: T[] }): T[] {
  return payload.data ?? [];
}

interface DailySummaryPayload {
  data?: {
    date: string;
    totals?: { calories?: number; proteinG?: number; carbsG?: number; fatG?: number };
    goals?: { calories?: number; proteinG?: number; carbsG?: number; fatG?: number };
  };
}

interface ApiMood {
  id: string;
  date?: string;
  fecha?: string;
  time?: string;
  hora?: string;
  state?: string;
  estado?: string;
  level?: number;
  nivel?: number;
  note?: string;
  nota?: string;
}

interface ApiMeal {
  id: string;
  consumedAt?: string;
  date?: string;
  fecha?: string;
  time?: string;
  hora?: string;
  name?: string;
  nombre?: string;
  calories?: number;
  calorias?: number;
  proteinG?: number;
  proteinas?: number;
  carbsG?: number;
  carbohidratos?: number;
  fatG?: number;
  grasas?: number;
  category?: string;
  categoria?: string;
  foto?: string;
}

interface ApiExercise {
  id: string;
  time?: string;
  hora?: string;
  type?: string;
  tipo?: string;
  name?: string;
  nombre?: string;
  durationMin?: number;
  duracionMin?: number;
  calories?: number;
  calorias?: number;
}

interface ApiActivityToday {
  steps?: number;
  pasos?: number;
  stepsGoal?: number;
  pasosObjetivo?: number;
  calories?: number;
  calorias?: number;
  activeMinutes?: number;
  minutosActivos?: number;
  exercises?: ApiExercise[];
  ejercicios?: ApiExercise[];
}

interface ApiWeekDay {
  day?: string;
  dia?: string;
  steps?: number;
  pasos?: number;
  calories?: number;
  calorias?: number;
}

interface ApiHydration {
  date?: string;
  fecha?: string;
  mlConsumed?: number;
  mlConsumidos?: number;
  mlGoal?: number;
  mlObjetivo?: number;
  pct?: number;
}

function mapMood(raw: ApiMood): Emocion {
  return {
    id: raw.id,
    fecha: raw.date ?? raw.fecha ?? '',
    hora: raw.time ?? raw.hora ?? '',
    estado: raw.state ?? raw.estado ?? 'neutral',
    nivel: raw.level ?? raw.nivel ?? 3,
    nota: raw.note ?? raw.nota,
  };
}

function _parseFechaHora(consumedAt?: string): { fecha: string; hora: string } {
  if (!consumedAt) return { fecha: '', hora: '' };
  const d = new Date(consumedAt);
  if (isNaN(d.getTime())) return { fecha: '', hora: '' };
  const fecha = d.toISOString().split('T')[0];
  const hora = d.toTimeString().slice(0, 5);
  return { fecha, hora };
}

function mapMeal(raw: ApiMeal): Comida {
  const fromConsumedAt = _parseFechaHora(raw.consumedAt);
  return {
    id: raw.id,
    fecha: raw.date ?? raw.fecha ?? fromConsumedAt.fecha,
    hora: raw.time ?? raw.hora ?? fromConsumedAt.hora,
    nombre: raw.name ?? raw.nombre ?? '',
    calorias: raw.calories ?? raw.calorias ?? 0,
    proteinas: raw.proteinG ?? raw.proteinas ?? 0,
    carbohidratos: raw.carbsG ?? raw.carbohidratos ?? 0,
    grasas: raw.fatG ?? raw.grasas ?? 0,
    categoria: raw.category ?? raw.categoria,
    foto: raw.foto,
  };
}

function mapExercise(raw: ApiExercise): Ejercicio {
  return {
    id: raw.id,
    hora: raw.time ?? raw.hora ?? '',
    tipo: raw.type ?? raw.tipo ?? '',
    nombre: raw.name ?? raw.nombre ?? '',
    duracionMin: raw.durationMin ?? raw.duracionMin ?? 0,
    calorias: raw.calories ?? raw.calorias ?? 0,
  };
}

function mapActivityToday(raw: ApiActivityToday): ActividadHoy {
  const ejercicios = (raw.exercises ?? raw.ejercicios ?? []).map(mapExercise);
  return {
    pasos: raw.steps ?? raw.pasos ?? 0,
    pasosObjetivo: raw.stepsGoal ?? raw.pasosObjetivo ?? 10_000,
    calorias: raw.calories ?? raw.calorias ?? 0,
    minutosActivos: raw.activeMinutes ?? raw.minutosActivos ?? 0,
    ejercicios,
  };
}

function mapWeekDay(raw: ApiWeekDay): { dia: string; pasos: number; calorias: number } {
  return {
    dia: raw.day ?? raw.dia ?? '',
    pasos: raw.steps ?? raw.pasos ?? 0,
    calorias: raw.calories ?? raw.calorias ?? 0,
  };
}

function mapHydration(raw: ApiHydration): Hidratacion & { pct: number } {
  const mlConsumidos = raw.mlConsumed ?? raw.mlConsumidos ?? 0;
  const mlObjetivo = raw.mlGoal ?? raw.mlObjetivo ?? 2500;
  const pct = raw.pct ?? Math.min(100, Math.round((mlConsumidos / mlObjetivo) * 100));
  return {
    fecha: raw.date ?? raw.fecha ?? new Date().toISOString().split('T')[0],
    mlConsumidos,
    mlObjetivo,
    pct,
  };
}

function mapDailySummaryToResumen(
  payload: DailySummaryPayload,
  fallbackGoals: Usuario['nutricion'],
): ResumenNutricion {
  const t = payload.data?.totals ?? {};
  const g = payload.data?.goals ?? {};
  const calorias = t.calories ?? 0;
  const proteinas = t.proteinG ?? 0;
  const carbohidratos = t.carbsG ?? 0;
  const grasas = t.fatG ?? 0;
  const objetivos = {
    caloriasObjetivo: g.calories ?? fallbackGoals.caloriasObjetivo,
    proteinasObjetivo: g.proteinG ?? fallbackGoals.proteinasObjetivo,
    carbohidratosObjetivo: g.carbsG ?? fallbackGoals.carbohidratosObjetivo,
    grasasObjetivo: g.fatG ?? fallbackGoals.grasasObjetivo,
  };
  return {
    calorias,
    proteinas,
    carbohidratos,
    grasas,
    objetivos,
    pctCalorias: Math.min(100, Math.round((calorias / objetivos.caloriasObjetivo) * 100)),
    pctProteinas: Math.min(100, Math.round((proteinas / objetivos.proteinasObjetivo) * 100)),
    pctCarbohidratos: Math.min(100, Math.round((carbohidratos / objetivos.carbohidratosObjetivo) * 100)),
    pctGrasas: Math.min(100, Math.round((grasas / objetivos.grasasObjetivo) * 100)),
  };
}

function mapMoodToApi(mood: Omit<Emocion, 'id' | 'fecha' | 'hora'>) {
  return {
    state: mood.estado,
    level: mood.nivel,
    note: mood.nota,
  };
}

export function mapMealToApi(meal: Omit<Comida, 'id' | 'fecha' | 'hora'>) {
  // NO enviar la foto base64 al crear la comida: el backend limita `foto` a 1024
  // chars (el campo es para una URL) → un data URL base64 daba 400 y la comida no
  // se guardaba. La foto se sube a Storage y se enlaza después vía updateMealFoto
  // (PATCH). Solo se incluye aquí si ya es una URL http(s) corta.
  const foto =
    typeof meal.foto === 'string' && /^https?:\/\//i.test(meal.foto) && meal.foto.length <= 1024
      ? meal.foto
      : undefined;
  // Acotar cada campo a los límites de createMealSchema (backend `.strict()`) para
  // que el payload SIEMPRE valide. Sin esto, p.ej. un nombre largo (los ítems del
  // análisis unidos por " + ") supera name.max(200) → 400 y la comida no se guarda.
  const clampNum = (n: unknown, min: number, max: number): number | undefined =>
    typeof n === 'number' && Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : undefined;
  const clampStr = (s: unknown, max: number): string | undefined =>
    typeof s === 'string' ? s.slice(0, max) : undefined;
  return {
    name: clampStr(meal.nombre, 200),
    calories: clampNum(meal.calorias, 0, 10000),
    proteinG: clampNum(meal.proteinas, 0, 1000),
    carbsG: clampNum(meal.carbohidratos, 0, 2000),
    fatG: clampNum(meal.grasas, 0, 1000),
    category: clampStr(meal.categoria, 100),
    ...(foto !== undefined ? { foto } : {}),
  };
}

function mapExerciseToApi(ej: Omit<Ejercicio, 'id' | 'hora'>) {
  return {
    type: ej.tipo,
    name: ej.nombre,
    durationMin: ej.duracionMin,
    calories: ej.calorias,
  };
}

interface ApiNutricionGoals {
  caloriasObjetivo?: number;
  proteinasObjetivo?: number;
  carbohidratosObjetivo?: number;
  grasasObjetivo?: number;
  hidratacionObjetivo?: number;
}

interface ApiUserProfile {
  id?: string;
  email?: string;
  displayName?: string;
  nombre?: string;
  apellidos?: string;
  pesoActual?: number;
  pesoObjetivo?: number;
  pesoInicial?: number;
  altura?: number;
  edad?: number;
  goal?: 'lose' | 'maintain' | 'gain';
  nutricion?: ApiNutricionGoals;
  onboardingCompletedAt?: string;
}

function mapUserProfile(raw: ApiUserProfile): Usuario {
  const nutricion = raw.nutricion ?? {};
  return {
    nombre:    raw.nombre ?? raw.displayName ?? '',
    apellidos: raw.apellidos ?? '',
    email:     raw.email ?? '',
    pesoActual:  raw.pesoActual  ?? 0,
    pesoObjetivo: raw.pesoObjetivo ?? 0,
    pesoInicial:  raw.pesoInicial  ?? raw.pesoActual ?? 0,
    altura: raw.altura ?? 0,
    edad:   raw.edad   ?? 0,
    goal:   raw.goal,
    onboardingCompletedAt: raw.onboardingCompletedAt,
    nutricion: {
      caloriasObjetivo:     nutricion.caloriasObjetivo     ?? 2000,
      proteinasObjetivo:    nutricion.proteinasObjetivo    ?? 120,
      carbohidratosObjetivo: nutricion.carbohidratosObjetivo ?? 250,
      grasasObjetivo:       nutricion.grasasObjetivo       ?? 65,
    },
  };
}

interface ApiWeightEntry {
  fecha?: string;
  pesoKg?: number;
}

function mapWeightEntry(raw: ApiWeightEntry, i: number): WeightEntry {
  return {
    date: raw.fecha ?? '',
    weight: raw.pesoKg ?? 0,
    week: `S${i + 1}`,
  };
}

/**
 * Frontera de persistencia del PERFIL. Exportada para poder clavarla en una
 * prueba: lo que esta funcion no copia al cuerpo NO LLEGA AL SERVIDOR, y no
 * avisa de ello — ver `perfil-frontera-persistencia.test.ts`.
 */
export function mapUsuarioToApi(u: Partial<Omit<Usuario, 'email'>>) {
  const body: Record<string, unknown> = {};
  if (u.nombre      !== undefined) body.nombre      = u.nombre;
  if (u.apellidos   !== undefined) body.apellidos   = u.apellidos;
  if (u.pesoActual  !== undefined) body.pesoActual  = u.pesoActual;
  if (u.pesoObjetivo !== undefined) body.pesoObjetivo = u.pesoObjetivo;
  if (u.pesoInicial !== undefined) body.pesoInicial = u.pesoInicial;
  if (u.altura      !== undefined) body.altura      = u.altura;
  if (u.edad        !== undefined) body.edad        = u.edad;
  if (u.goal        !== undefined) body.goal        = u.goal;
  if (u.nutricion   !== undefined) body.nutricion   = u.nutricion;
  if (u.onboardingCompletedAt !== undefined) body.onboardingCompletedAt = u.onboardingCompletedAt;
  return body;
}

// ── Análisis nutricional por IA ────────────────────────────────────────────
// Forma del job tal como lo devuelve el backend (GET /nutrition-analyses/:id).
// Defensivo: acepta `result` ya normalizado o `resultPayload` crudo del
// orquestador (donde el item trae `energyKcalExpected`).
interface ApiAnalysisItem {
  label?: string;
  name?: string;
  estimatedQuantity?: { amount?: number; unit?: string };
  energyKcal?: number;
  energyKcalExpected?: number;
  energyKcalRange?: { low?: number; high?: number };
  macros?: { proteinG?: number; carbsG?: number; fatG?: number; fiberG?: number };
  confidence?: { score?: number; level?: string };
}
interface ApiAnalysisResult {
  schemaVersion?: string;
  items?: ApiAnalysisItem[];
  totals?: { energyKcal?: number; proteinG?: number; carbsG?: number; fatG?: number; fiberG?: number };
  overallConfidence?: { score?: number; level?: string };
  warnings?: string[];
  notes?: string;
  goalApplied?: string;
}
interface ApiAnalysisJob {
  id: string;
  status?: string;
  photoId?: string;
  mealId?: string;
  result?: ApiAnalysisResult;
  resultPayload?: ApiAnalysisResult;
  errorMessage?: string;
  errorCode?: string;
  error?: string;
  message?: string;
}

/** Respuesta de POST /photos/upload-url (GCS v4 signed URL, action: write). */
interface UploadUrlResponse {
  photoId: string;
  uploadUrl: string;
  storagePath?: string;
  expiresAt?: string;
}

/** Convierte un data URL (base64) en Blob para subirlo por PUT a la URL firmada. */
function dataUrlToBlob(dataUrl: string, contentType: string): Blob {
  const base64 = dataUrl.split(',')[1] ?? '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

/**
 * SHA-256 hexadecimal de los bytes que se van a subir.
 *
 * Va con la foto para poder responder después a dos preguntas que hoy no tienen
 * respuesta: si el objeto del bucket sigue siendo el que se subió, y si dos
 * documentos apuntan al mismo contenido.
 *
 * Devuelve `null` si el entorno no ofrece `crypto.subtle` (contexto no seguro,
 * navegador antiguo, jsdom): el hash es deseable, no un requisito para guardar
 * la comida del paciente. Se registra como ausente, no como falso.
 */
async function sha256Hex(blob: Blob): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null;
    const buf = await blob.arrayBuffer();
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/** Tope de tamaño de foto (8 MB). Emparejado con la validación de backend (Q2). */
export const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

/** Tamaño en bytes de un data URL base64 sin decodificarlo (aprox. exacto por el padding). */
export function base64DataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.split(',')[1] ?? '';
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/**
 * Rechaza en el cliente una foto no-imagen o demasiado grande ANTES de pedir la
 * URL de subida y transferir bytes. Evita gastar cuota IA y una subida en balde
 * (el backend también valida, Q2; esto es la primera línea, más rápida y clara).
 */
export function assertPhotoWithinLimits(dataUrl: string, contentType: string): void {
  if (!/^image\//.test(contentType)) {
    throw new ApiError('El archivo no es una imagen válida.', 415, 'PHOTO_INVALID_TYPE');
  }
  const bytes = base64DataUrlBytes(dataUrl);
  if (bytes > MAX_PHOTO_BYTES) {
    const mb = (bytes / (1024 * 1024)).toFixed(1);
    throw new ApiError(
      `La foto es demasiado grande (${mb} MB, máx 8 MB). Vuelve a hacerla o recórtala.`,
      413,
      'PHOTO_TOO_LARGE',
    );
  }
}

function mapAnalysisItem(raw: ApiAnalysisItem): AnalysisItem {
  const score = raw.confidence?.score ?? 0.5;
  const level = (raw.confidence?.level as AnalysisItem['confidence']['level'] | undefined)
    ?? (score >= 0.8 ? 'high' : score >= 0.5 ? 'medium' : 'low');
  const unit = (raw.estimatedQuantity?.unit as AnalysisItem['estimatedQuantity']['unit'] | undefined) ?? 'g';
  return {
    label: raw.label ?? raw.name ?? 'Alimento',
    estimatedQuantity: { amount: raw.estimatedQuantity?.amount ?? 0, unit },
    energyKcal: Math.round(raw.energyKcal ?? raw.energyKcalExpected ?? 0),
    energyKcalRange: raw.energyKcalRange
      ? { low: raw.energyKcalRange.low ?? 0, high: raw.energyKcalRange.high ?? 0 }
      : undefined,
    macros: {
      proteinG: raw.macros?.proteinG ?? 0,
      carbsG: raw.macros?.carbsG ?? 0,
      fatG: raw.macros?.fatG ?? 0,
      fiberG: raw.macros?.fiberG,
    },
    confidence: { score, level },
  };
}

function mapAnalysisResult(raw: ApiAnalysisResult): NutritionAnalysisResult {
  const items = (raw.items ?? []).map(mapAnalysisItem);
  const totals = raw.totals ?? items.reduce(
    (acc, it) => ({
      energyKcal: acc.energyKcal + it.energyKcal,
      proteinG: acc.proteinG + it.macros.proteinG,
      carbsG: acc.carbsG + it.macros.carbsG,
      fatG: acc.fatG + it.macros.fatG,
      fiberG: (acc.fiberG ?? 0) + (it.macros.fiberG ?? 0),
    }),
    { energyKcal: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0 },
  );
  const score = raw.overallConfidence?.score ?? 0.5;
  return {
    schemaVersion: raw.schemaVersion ?? 'unknown',
    items,
    totals: {
      energyKcal: Math.round(totals.energyKcal ?? 0),
      proteinG: totals.proteinG ?? 0,
      carbsG: totals.carbsG ?? 0,
      fatG: totals.fatG ?? 0,
      fiberG: totals.fiberG,
    },
    overallConfidence: {
      score,
      level: (raw.overallConfidence?.level as NutritionAnalysisResult['overallConfidence']['level'] | undefined)
        ?? (score >= 0.8 ? 'high' : score >= 0.5 ? 'medium' : 'low'),
    },
    warnings: raw.warnings ?? [],
    notes: raw.notes,
    goalApplied: raw.goalApplied as NutritionAnalysisResult['goalApplied'],
  };
}

function mapAnalysisJob(raw: ApiAnalysisJob): NutritionAnalysisJob {
  const payload = raw.result ?? raw.resultPayload;
  return {
    id: raw.id,
    status: (raw.status as AnalysisStatus | undefined) ?? 'queued',
    photoId: raw.photoId,
    mealId: raw.mealId,
    result: payload ? mapAnalysisResult(payload) : undefined,
    error: raw.errorMessage ?? raw.error ?? raw.message,
    errorCode: raw.errorCode,
  };
}

export type ConsentType =
  | 'terms_of_service'
  | 'privacy_policy'
  | 'health_data_processing'
  | 'image_storage'
  | 'image_calibration'      // opt-in: imagen anonimizada para calibración del análisis
  | 'medical_consultation'   // premium: consulta médica + datos clínicos (independiente)
  | 'research_participation'
  | 'marketing';

export interface ConsentRecord {
  id: string;
  consentType: ConsentType;
  status: 'granted' | 'revoked' | 'pending';
  version: string;
}

// ── HD-040 · Firma electrónica del consentimiento (Email OTP) ───────────────
// Contrato congelado en docs/specs/HD-040-consentimiento-firma.md §3.

/** Decisión por tipo de consentimiento al firmar. */
export type ConsentDecision = 'granted' | 'denied';
export type ConsentDecisionMap = Partial<Record<ConsentType, ConsentDecision>>;

/** Registro de firma devuelto por GET /consents/signature/current. */
export interface ConsentSignature {
  signatureId: string;
  signedAt: string;
  documentVersion: string;
  method: string;                 // p.ej. 'email_otp'
  consents: ConsentDecisionMap;
  signedDocumentUrl?: string;     // PDF firmado (Storage)
  documentHash?: string;          // sha256 del documento firmado
  emailChannel?: string;          // email al que se envió la copia
}

export interface ApiReportSummary {
  days: number;
  meals: {
    count: number;
    totalKcal: number;
    avgKcalPerDay: number;
    avgProteinG: number;
    avgCarbsG: number;
    avgFatG: number;
  };
  weight: { entries: number; startKg?: number; endKg?: number; changeKg?: number };
}

export interface ApiReport {
  id: string;
  reportType: 'summary' | 'export' | 'custom';
  format: 'json' | 'pdf' | 'csv';
  status: 'pending' | 'ready' | 'failed';
  periodFrom?: string;
  periodTo?: string;
  storagePath?: string;
  downloadUrl?: string;
  summary?: ApiReportSummary;
  createdAt: string;
  updatedAt: string;
}

// ── HD-045 · Asistente del programa (chatbot RAG, B4) ───────────────────────
// Wire types del contrato REAL de HOY, leídos de
// services/helpydoc-backend/src/modules/asistente/asistente.schema.ts (no de
// una suposición): `@helpydoc/contracts` NO es dependencia de `apps/web`
// todavía (ver docs/relevos/BITACORA-HD-045-ASISTENTE.md, entrada B0/B3) y ese
// paquete tampoco exporta un tipo para este wire format HTTP en sí — así que,
// igual que el resto de este fichero (`ApiMood`, `ApiMeal`, …), estos tipos
// son locales y deliberadamente NO importados de `packages/contracts`.
export type CategoriaPuertaClinica =
  | 'diagnostico' | 'tratamiento' | 'medicacion' | 'sintomas' | 'urgencia' | 'terceros';

export interface CitaAsistente {
  fragmentoId: string;
  tituloFuente: string;
  version: string;
}

export interface DecisionPuertaAsistente {
  disparada: boolean;
  categoria?: CategoriaPuertaClinica;
  reglaId?: string;
  reglaFuente?: string;
  reglaVersion?: string;
  origen?: 'entrada' | 'salida';
  textoDerivacion?: string;
}

export interface RespuestaAsistente {
  texto: string;
  citas: CitaAsistente[];
  decisionPuerta: DecisionPuertaAsistente;
  fueraDeAmbito?: boolean;
  sinFragmentoSuficiente?: boolean;
  conversacionId: string;
}

export interface PreguntaAsistenteInput {
  texto: string;
  conversacionId?: string;
}

export const ApiClient = {
  isConfigured(): boolean {
    return Boolean(API_BASE);
  },

  // ── HD-045 · Asistente del programa (chatbot RAG, B4) ─────────────────────
  /**
   * POST /asistente/preguntas. Éxito = 201 `{ data: RespuestaAsistente }`
   * (`unwrapData` ya sabe leer ese envelope). Errores conocidos, tal y como
   * los deja el pipeline del backend (B3, verificado ejecutando tests, no
   * solo leyendo código): 404 si `ASISTENTE_ENABLED` no es 'true' en el
   * servidor (fail-closed — la ruta no existe); 400 si el texto está vacío o
   * pasa de 1000 caracteres (validación Zod, corre ANTES que la auth); 401
   * sin token válido; 429 `TOO_MANY_REQUESTS` — dos motivos indistinguibles
   * por `code`, solo por `message` (pregunta > 500 caracteres, o cupo diario
   * de 30 preguntas/isid agotado); 500 en error no controlado. `request()` ya
   * traduce el 429 a un mensaje neutro para el usuario (`friendlyApiMessage`).
   */
  async preguntarAsistente(input: PreguntaAsistenteInput): Promise<RespuestaAsistente> {
    const payload = await request<{ data?: RespuestaAsistente }>('/asistente/preguntas', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return unwrapData(payload);
  },

  /**
   * Lista los consentimientos del usuario (backend RGPD).
   * `GET /consents` devuelve el envelope paginado `{ items, total, page,
   * pageSize, hasMore }` en la RAÍZ, sin `data` (consents.service.ts,
   * createListResponseSchema). Se mantienen las variantes con `data` por
   * compatibilidad. HD-WO-MET-08: antes solo leía `data` → siempre `[]`.
   */
  async listConsents(): Promise<ConsentRecord[]> {
    const payload = await request<
      | { items?: ConsentRecord[]; data?: { items?: ConsentRecord[] } | ConsentRecord[] }
      | ConsentRecord[]
    >('/consents?pageSize=50');
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.items)) return payload.items;
    const data = payload.data;
    if (Array.isArray(data)) return data;
    return data?.items ?? [];
  },

  /** Registra (concede) un consentimiento. */
  async createConsent(input: {
    consentType: ConsentType;
    status: 'granted' | 'revoked';
    version: string;
    source?: string;
  }): Promise<void> {
    await request('/consents', {
      method: 'POST',
      body: JSON.stringify({ grantedAt: new Date().toISOString(), source: 'web_app', ...input }),
    });
  },

  // ── HD-040 · Firma electrónica (Email OTP) — contrato §3 ─────────────────

  /** Solicita un código OTP al email del usuario autenticado. */
  async requestConsentOtp(documentVersion: string): Promise<{ requestId: string; expiresInSec: number }> {
    const payload = await request<{ data?: { requestId: string; expiresInSec: number } }>(
      '/consents/sign/request-otp',
      { method: 'POST', body: JSON.stringify({ documentVersion }) },
    );
    // El backend puede envolver en { data } o devolver plano; se aceptan ambos.
    const d = payload.data ?? (payload as unknown as { requestId: string; expiresInSec: number });
    return { requestId: d.requestId, expiresInSec: d.expiresInSec };
  },

  /** Verifica el OTP y crea el registro de firma + consents individuales. */
  async verifyConsentSignature(input: {
    requestId: string;
    code: string;
    consents: ConsentDecisionMap;
    documentVersion: string;
  }): Promise<{ signatureId: string; signedAt: string; signedDocumentUrl?: string }> {
    const payload = await request<{ data?: { signatureId: string; signedAt: string; signedDocumentUrl?: string } }>(
      '/consents/sign/verify',
      { method: 'POST', body: JSON.stringify(input) },
    );
    const d = payload.data ?? (payload as unknown as { signatureId: string; signedAt: string; signedDocumentUrl?: string });
    return { signatureId: d.signatureId, signedAt: d.signedAt, signedDocumentUrl: d.signedDocumentUrl };
  },

  /** Devuelve la firma vigente, o null si el usuario no ha firmado (404). */
  async getConsentSignatureCurrent(): Promise<ConsentSignature | null> {
    try {
      const payload = await request<{ data?: ConsentSignature }>('/consents/signature/current');
      return payload.data ?? (payload as unknown as ConsentSignature);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /** Reenvía por email la copia PDF firmada. */
  async resendConsentCopy(): Promise<void> {
    // Cuerpo vacío explícito: `request` fija Content-Type application/json y
    // Fastify rechaza (500) un POST json sin body.
    await request('/consents/signature/resend-copy', { method: 'POST', body: JSON.stringify({}) });
  },

  async getDailySummary(date: string, fallbackGoals: Usuario['nutricion']): Promise<ResumenNutricion> {
    const qs = new URLSearchParams({ date });
    const payload = await request<DailySummaryPayload>(`/meals/daily-summary?${qs}`);
    return mapDailySummaryToResumen(payload, fallbackGoals);
  },

  async getMoodHistory(days: number): Promise<Emocion[]> {
    const qs = new URLSearchParams({ days: String(days) });
    const payload = await request<{ data?: ApiMood[] }>(`/moods?${qs}`);
    return unwrapList(payload).map(mapMood);
  },

  async saveMood(mood: Omit<Emocion, 'id' | 'fecha' | 'hora'>): Promise<Emocion> {
    const payload = await request<{ data?: ApiMood }>('/moods', {
      method: 'POST',
      body: JSON.stringify(mapMoodToApi(mood)),
    });
    return mapMood(unwrapData(payload));
  },

  async getActivityToday(): Promise<ActividadHoy | null> {
    const payload = await request<{ data?: ApiActivityToday | null }>('/activities/today');
    const raw = payload.data;
    return raw ? mapActivityToday(raw) : null;
  },

  async getActivityWeek(): Promise<{ dia: string; pasos: number; calorias: number }[]> {
    const payload = await request<{ data?: ApiWeekDay[] }>('/activities/week');
    return unwrapList(payload).map(mapWeekDay);
  },

  async saveExercise(ej: Omit<Ejercicio, 'id' | 'hora'>): Promise<Ejercicio> {
    const payload = await request<{ data?: ApiExercise }>('/activities/exercises', {
      method: 'POST',
      body: JSON.stringify(mapExerciseToApi(ej)),
    });
    return mapExercise(unwrapData(payload));
  },

  async getMealsToday(): Promise<Comida[]> {
    const payload = await request<{ data?: ApiMeal[] }>('/meals/today');
    return unwrapList(payload).map(mapMeal);
  },

  async getMeals(opts?: { from?: string; to?: string; pageSize?: number }): Promise<Comida[]> {
    const params = new URLSearchParams({ pageSize: String(opts?.pageSize ?? 100) });
    if (opts?.from) params.set('from', opts.from);
    if (opts?.to)   params.set('to', opts.to);
    // GET /meals devuelve un envelope PAGINADO { items, total, page, ... } SIN
    // envoltura `data` (a diferencia de /meals/today, que sí es { data: [...] }).
    // Leer `items` con fallbacks defensivos; antes leía `data` → siempre [] → el
    // Diario mostraba 0 aunque hubiera comidas.
    const payload = await request<{ items?: ApiMeal[]; data?: ApiMeal[] } | ApiMeal[]>(`/meals?${params}`);
    const list = Array.isArray(payload) ? payload : (payload.items ?? payload.data ?? []);
    return list.map(mapMeal);
  },

  async saveMeal(meal: Omit<Comida, 'id' | 'fecha' | 'hora'>): Promise<Comida> {
    const payload = await request<{ data?: ApiMeal }>('/meals', {
      method: 'POST',
      body: JSON.stringify(mapMealToApi(meal)),
    });
    return mapMeal(unwrapData(payload));
  },

  async deleteMeal(id: string): Promise<void> {
    await request(`/meals/${id}`, { method: 'DELETE' });
  },

  async updateMealFoto(id: string, foto: string): Promise<void> {
    await request(`/meals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ foto }),
    });
  },

  /** Actualiza nombre/macros de una comida (PATCH /meals/:id). Solo envía los campos
   *  provistos; el backend valida con updateMealSchema (createMealSchema parcial). */
  async updateMeal(
    id: string,
    patch: Partial<Pick<Comida, 'nombre' | 'calorias' | 'proteinas' | 'carbohidratos' | 'grasas'>>,
  ): Promise<Comida> {
    const body: Record<string, unknown> = {};
    if (patch.nombre !== undefined) body.name = patch.nombre.slice(0, 200);
    if (patch.calorias !== undefined) body.calories = patch.calorias;
    if (patch.proteinas !== undefined) body.proteinG = patch.proteinas;
    if (patch.carbohidratos !== undefined) body.carbsG = patch.carbohidratos;
    if (patch.grasas !== undefined) body.fatG = patch.grasas;
    const payload = await request<{ data?: ApiMeal }>(`/meals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return mapMeal(unwrapData(payload));
  },

  async getHydrationToday(): Promise<Hidratacion & { pct: number }> {
    const payload = await request<{ data?: ApiHydration }>('/hydration/today');
    return mapHydration(unwrapData(payload));
  },

  async updateHydration(mlAnadir: number): Promise<Hidratacion> {
    const payload = await request<{ data?: ApiHydration }>('/hydration', {
      method: 'PATCH',
      body: JSON.stringify({ mlAnadir }),
    });
    const mapped = mapHydration(unwrapData(payload));
    return {
      fecha: mapped.fecha,
      mlConsumidos: mapped.mlConsumidos,
      mlObjetivo: mapped.mlObjetivo,
    };
  },

  async getMe(): Promise<Usuario> {
    const payload = await request<{ data?: ApiUserProfile }>('/users/me');
    return mapUserProfile(unwrapData(payload));
  },

  /**
   * Estado de elegibilidad clínica del usuario autenticado (ADR-018/ADR-019).
   * `elegible` ya viene resuelto por el backend — el front no necesita conocer
   * los 9 estados de `MAQUINA_ELEGIBILIDAD`, solo si puede participar o sigue
   * en "solo visualización".
   */
  async getEligibilidadPropia(): Promise<{ estado: string; elegible: boolean }> {
    const payload = await request<{ data?: { estado: string; elegible: boolean } }>('/eligibility/me');
    return unwrapData(payload);
  },

  /**
   * Sella la valoración inicial EN EL SERVIDOR, que es donde la regla se
   * impone: el sello y la línea roja del trastorno alimentario los decide el
   * backend sobre lo que recibe, no el navegador que se lo envía.
   *
   * Un segundo intento responde 409 `REGISTRO_YA_SELLADO`. Eso no es un fallo
   * que haya que reintentar: es la regla funcionando.
   */
  async confirmarValoracionInicial(datos: {
    sexo?: 'hombre' | 'mujer';
    edad?: number;
    pesoKg?: number;
    tallaCm?: number;
    cinturaCm?: number;
    comorbilidades: string[];
  }): Promise<void> {
    await request('/valoracion', { method: 'POST', body: JSON.stringify(datos) });
  },

  async getPesoHistorico(): Promise<WeightEntry[]> {
    const payload = await request<{ data?: ApiWeightEntry[] }>('/weight');
    return unwrapList(payload).map(mapWeightEntry);
  },

  /** Crea/actualiza la entrada de peso de una fecha (POST /weight → serie temporal). */
  async saveWeight(fecha: string, pesoKg: number): Promise<void> {
    await request('/weight', {
      method: 'POST',
      body: JSON.stringify({ fecha, pesoKg }),
    });
  },

  // ── Platos saludables (catálogo curado) ─────────────────────────────────────
  async getPlatos(filtros: { categoria?: string; etiqueta?: string; busqueda?: string } = {}): Promise<Plato[]> {
    const qs = new URLSearchParams();
    if (filtros.categoria) qs.set('categoria', filtros.categoria);
    if (filtros.etiqueta) qs.set('etiqueta', filtros.etiqueta);
    if (filtros.busqueda) qs.set('busqueda', filtros.busqueda);
    const q = qs.toString();
    const payload = await request<{ data?: Plato[] }>(`/platos${q ? `?${q}` : ''}`);
    return unwrapList(payload);
  },

  // ── Informes ────────────────────────────────────────────────────────────────

  /** Lista los informes del usuario (GET /reports), más recientes primero. */
  async listReports(): Promise<ApiReport[]> {
    const payload = await request<{ data?: { items?: ApiReport[] } | ApiReport[] } | ApiReport[]>('/reports');
    const data = (payload as { data?: unknown })?.data ?? payload;
    const items = Array.isArray(data) ? data : ((data as { items?: ApiReport[] })?.items ?? []);
    return items as ApiReport[];
  },

  /**
   * Solicita un informe (POST /reports). Para 'summary' el backend lo computa en
   * el acto desde comidas+peso del periodo y devuelve status 'ready' con summary.
   */
  async createReport(input: {
    reportType: 'summary' | 'export';
    format?: 'json' | 'pdf' | 'csv';
    periodFrom?: string;
    periodTo?: string;
  }): Promise<ApiReport> {
    const payload = await request<{ data?: ApiReport } | ApiReport>('/reports', {
      method: 'POST',
      body: JSON.stringify({ format: 'json', ...input }),
    });
    return unwrapData(payload as { data?: ApiReport });
  },

  async updateMe(changes: Partial<Omit<Usuario, 'email'>>): Promise<Usuario> {
    const payload = await request<{ data?: ApiUserProfile }>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(mapUsuarioToApi(changes)),
    });
    return mapUserProfile(unwrapData(payload));
  },

  async bootstrapUser(profile: Partial<Omit<Usuario, 'email'>>): Promise<Usuario> {
    const payload = await request<{ data?: ApiUserProfile }>('/users', {
      method: 'POST',
      body: JSON.stringify(mapUsuarioToApi(profile)),
    });
    return mapUserProfile(unwrapData(payload));
  },

  // ── Base de datos de alimentos ──────────────────────────────────────────────

  /**
   * Busca alimentos en la base de datos compartida del backend.
   * El backend consulta primero `food_items` (Firestore) y si no encuentra
   * llama a FatSecret/USDA y guarda el resultado en el catálogo.
   */
  async searchFoods(query: string): Promise<import('./DataService').FoodItem[]> {
    interface ApiFoodItem {
      id: string;
      name: string;
      displayName?: string;
      aliases?: string[];
      category?: string;
      kcalPer100g: number;
      proteinPer100g: number;
      carbsPer100g: number;
      fatPer100g: number;
      fiberPer100g?: number;
      typicalServingG?: number;
      typicalServingLabel?: string;
      source?: string;
      analysisCount?: number;
      verifiedByNutritionist?: boolean;
    }
    const payload = await request<{ data?: ApiFoodItem[]; items?: ApiFoodItem[] }>(
      `/foods/search?q=${encodeURIComponent(query)}&limit=10`,
    );
    const items: ApiFoodItem[] = payload.data ?? payload.items ?? [];
    return items.map(f => ({
      id: f.id,
      name: f.name,
      displayName: f.displayName ?? f.name,
      aliases: f.aliases ?? [],
      category: (f.category ?? 'otro') as import('./DataService').FoodCategory,
      kcalPer100g: f.kcalPer100g,
      proteinPer100g: f.proteinPer100g,
      carbsPer100g: f.carbsPer100g,
      fatPer100g: f.fatPer100g,
      fiberPer100g: f.fiberPer100g,
      typicalServingG: f.typicalServingG ?? 100,
      typicalServingLabel: f.typicalServingLabel ?? '100 g',
      source: (f.source ?? 'usda') as import('./DataService').FoodSource,
      analysisCount: f.analysisCount ?? 0,
      verifiedByNutritionist: f.verifiedByNutritionist ?? false,
    }));
  },

  /**
   * Contribuye un alimento al catálogo compartido.
   * Normalmente el backend lo hace automáticamente tras el análisis IA.
   * Este endpoint permite contribución manual desde el frontend.
   */
  async contribuirAlimento(food: Omit<import('./DataService').FoodItem, 'id' | 'analysisCount' | 'verifiedByNutritionist'>): Promise<void> {
    await request('/foods', {
      method: 'POST',
      body: JSON.stringify(food),
    });
  },

  // ── Análisis nutricional por IA ──────────────────────────────────────────────

  /**
   * ÚNICO camino para subir una foto. Tres pasos:
   *  1. POST /photos/upload-url { contentType, fileName, sizeBytes, contentHash,
   *     mealId? } → URL firmada (GCS v4, action: write) + photoId + storagePath.
   *     El backend crea AQUÍ el documento de metadatos y sella el consentimiento
   *     RGPD vigente, antes de que exista un solo byte en el bucket.
   *  2. PUT de los bytes a esa URL firmada (sin Authorization: la firma va en la
   *     propia URL; el Content-Type debe coincidir con el firmado).
   *  3. PATCH /photos/:id { processingStatus: 'uploaded' } → confirma la subida.
   *     Sin esta confirmación el documento se queda en `pending` y el
   *     reconciliador lo trata como subida abandonada.
   *
   * Devuelve `storagePath` además del `photoId` porque la ruta es el
   * identificador CANÓNICO de la imagen: es lo que se guarda con la comida y lo
   * que permite borrarla. La URL de descarga se resuelve solo para pintarla.
   */
  async uploadPhoto(
    dataUrl: string,
    opts: { fileName?: string; mealId?: string } = {},
  ): Promise<{ photoId: string; storagePath: string }> {
    const contentType = /^data:(.+?);base64,/.exec(dataUrl)?.[1] ?? 'image/jpeg';
    assertPhotoWithinLimits(dataUrl, contentType);
    const ext = contentType.split('/')[1]?.split('+')[0] ?? 'jpg';
    const fileName = opts.fileName ?? `plato-${Date.now()}.${ext}`;
    const blob = dataUrlToBlob(dataUrl, contentType);

    const reqBody: Record<string, unknown> = { contentType, fileName, sizeBytes: blob.size };
    const contentHash = await sha256Hex(blob);
    if (contentHash) reqBody.contentHash = contentHash;
    if (opts.mealId) reqBody.mealId = opts.mealId;

    const payload = await request<{ data?: UploadUrlResponse } | UploadUrlResponse>('/photos/upload-url', {
      method: 'POST',
      body: JSON.stringify(reqBody),
    });
    const res = (payload as { data?: UploadUrlResponse }).data ?? (payload as UploadUrlResponse);
    if (!res?.photoId || !res?.uploadUrl || !res?.storagePath) {
      throw new ApiError('Respuesta de /photos/upload-url incompleta', 502, 'INVALID_RESPONSE');
    }

    const putRes = await fetch(res.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
    if (!putRes.ok) {
      throw new ApiError(`Subida de la imagen a Storage falló (HTTP ${putRes.status})`, putRes.status, 'UPLOAD_FAILED');
    }

    // Confirmación. Best-effort a propósito: los bytes YA están en el bucket y el
    // documento YA existe, así que fallar aquí no pierde nada — solo deja el
    // estado en `pending`, que es reconciliable. Tirar la subida entera por no
    // poder actualizar un campo sería peor para el paciente.
    await request(`/photos/${res.photoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ processingStatus: 'uploaded' }),
    }).catch(() => {});

    return { photoId: res.photoId, storagePath: res.storagePath };
  },

  /** Crea un job de análisis para una foto ya subida. POST /nutrition-analysis. */
  async createAnalysis(input: {
    photoId: string;
    mealId?: string;
    userNote?: string;
    plateSizeHint?: string;
    /** Prior geométrico calculado en el navegador (ADR-016 §D2). */
    estimacionPrevia?: EstimacionPreviaTransmitida;
    /** Imagen ya recortada y reescalada para el modelo (§D3/§D4). */
    entradaModelo?: EntradaModeloTransmitida;
  }): Promise<NutritionAnalysisJob> {
    const body: Record<string, unknown> = { photoId: input.photoId };
    if (input.mealId) body.mealId = input.mealId;
    const rp: Record<string, string> = {};
    if (input.userNote)      rp.userNote      = input.userNote;
    if (input.plateSizeHint) rp.plateSizeHint = input.plateSizeHint;
    if (Object.keys(rp).length) body.requestPayload = rp;
    // Campos propios y NO claves de `requestPayload`: ése se persiste y topa
    // los textos en 2 000 caracteres. Ver ADR-016 §D4.
    if (input.estimacionPrevia) body.estimacionPrevia = input.estimacionPrevia;
    if (input.entradaModelo)    body.entradaModelo    = input.entradaModelo;
    const payload = await request<{ data?: ApiAnalysisJob } | ApiAnalysisJob>('/nutrition-analysis', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const raw = (payload as { data?: ApiAnalysisJob }).data ?? (payload as ApiAnalysisJob);
    return mapAnalysisJob(raw);
  },

  /** Consulta el estado/resultado de un job. GET /nutrition-analysis/:id (polling). */
  async getAnalysis(id: string): Promise<NutritionAnalysisJob> {
    const payload = await request<{ data?: ApiAnalysisJob } | ApiAnalysisJob>(
      `/nutrition-analysis/${encodeURIComponent(id)}`,
    );
    const raw = (payload as { data?: ApiAnalysisJob }).data ?? (payload as ApiAnalysisJob);
    return mapAnalysisJob(raw);
  },
};

/**
 * HD-042 · El cliente de salud (`services/health/api.ts`) vive en su propio
 * módulo por tamaño, pero reutiliza EXACTAMENTE este `request`: mismo manejo de
 * token, mismos mensajes de error amigables, misma base de URL. Duplicarlo
 * habría creado una segunda política de autenticación.
 */
export { request as apiRequest };
